import { useContext, useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import {
  AddressContext,
  AddressProviderValue,
} from 'pages/address/address.provider'
import {
  useGetAllCompaniesQuery,
  useGetCarrierCompaniesByUserQuery,
  useGetUserCompanyAssociationByUserQuery,
} from 'store/company/company.api'
import {
  CarrierCompany,
  Company,
  CompanyAssociation,
} from 'store/company/company.types'
import { getUserId } from 'utils/common'

export type CustomerListItem = {
  association: CompanyAssociation
  companyName: string
}

export const useCustomerList = () => {
  const { filterType, searchText } = useContext(
    AddressContext
  ) as AddressProviderValue

  const userId = getUserId()
  const {
    data: associations,
    isLoading,
    refetch,
  } = useGetUserCompanyAssociationByUserQuery(
    userId ? { userId } : skipToken
  )
  const { data: allCompanies } = useGetAllCompaniesQuery()

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>()
    ;(allCompanies ?? []).forEach((company: Company) => {
      map.set(company.id, company.companyName)
    })
    return map
  }, [allCompanies])

  const customers = useMemo((): CustomerListItem[] => {
    if (!associations) return []

    const customerAssociations = associations.filter(
      (company) =>
        company.association === 'Customer' || company.association === 'Both'
    )

    const byFactoring =
      filterType === 'factoring'
        ? customerAssociations.filter((company) => company.factoringEnabled)
        : customerAssociations.filter((company) => !company.factoringEnabled)

    const query = searchText.trim().toLowerCase()
    const filtered = query
      ? byFactoring.filter((company) => {
          const companyName = companyNameById.get(company.companyId) ?? ''
          return companyName.toLowerCase().includes(query)
        })
      : byFactoring

    return filtered
      .map((association) => ({
        association,
        companyName: companyNameById.get(association.companyId) ?? '',
      }))
      .sort((a, b) => a.companyName.localeCompare(b.companyName))
  }, [associations, filterType, searchText, companyNameById])

  return { customers, isLoading, refetch, companyNameById }
}

export const useSelectedCustomer = () => {
  const { selectedAssociationId } = useContext(
    AddressContext
  ) as AddressProviderValue
  const { customers } = useCustomerList()

  return useMemo(() => {
    if (!selectedAssociationId) return null
    return (
      customers.find(
        (item) => item.association.id === selectedAssociationId
      ) ?? null
    )
  }, [customers, selectedAssociationId])
}

export type CarrierListItem = {
  carrier: CarrierCompany
}

export const useCarrierList = () => {
  const { searchText } = useContext(AddressContext) as AddressProviderValue

  const userId = getUserId()
  const {
    data: carriers,
    isLoading,
    refetch,
  } = useGetCarrierCompaniesByUserQuery(userId ? { userId } : skipToken)

  const filteredCarriers = useMemo((): CarrierListItem[] => {
    if (!carriers) return []

    const query = searchText.trim().toLowerCase()
    const filtered = query
      ? carriers.filter((carrier) => {
          const name = carrier.companyName.toLowerCase()
          const dispatch = (carrier.dispatchName ?? '').toLowerCase()
          return name.includes(query) || dispatch.includes(query)
        })
      : carriers

    return filtered
      .map((carrier) => ({ carrier }))
      .sort((a, b) =>
        a.carrier.companyName.localeCompare(b.carrier.companyName)
      )
  }, [carriers, searchText])

  return { carriers: filteredCarriers, isLoading, refetch }
}

export const useSelectedCarrier = () => {
  const { selectedAssociationId } = useContext(
    AddressContext
  ) as AddressProviderValue
  const { carriers } = useCarrierList()

  return useMemo(() => {
    if (!selectedAssociationId) return null
    return (
      carriers.find(
        (item) => item.carrier.associationId === selectedAssociationId
      ) ?? null
    )
  }, [carriers, selectedAssociationId])
}
